import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { ICommodityFormValues } from '../../../../@types/model/masterData/commodity/commodityFormValues';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, ProjectDialog, ArrayHelper, IOptionType, MuiAutocompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { Draggable, Droppable, DragDropContext, DropResult } from 'react-beautiful-dnd';
import { ICommodityConditionRel } from '../../../../@types/model/masterData/commodityConditionRel/commodityConditionRel';
import { Card, Icon, Typography } from '@mui/material';
import lodash from 'lodash';
import { dataSetCommodityConditions } from '../../../../store/masterData/Actions';
import { MAX_COMMODITY_CONDITION_COLUMNS, renderCommodityConditionsHeader } from '../../../../services/compliance/documentService';
import { Document, Page, PDFViewer } from '@react-pdf/renderer';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import CommodityConditionHttpService from '../../../../services/http/masterData/commodityConditionHttpService';
import CommodityConditionForm from '../../commodityCondition/form/CommodityConditionForm';
import CustomTooltip from '../../../../components/tooltip/tooltip';
import { IApiError } from '../../../../@types/other';
import CommodityConditionModelHelper from '../../../../@types/model/masterData/commodityCondition/commodityConditionModelHelper';
import { ICommodityCondition } from '../../../../@types/model/masterData/commodityCondition/commodityCondition';
import { ICommodityConditionFormValues } from '../../../../@types/model/masterData/commodityCondition/commodityConditionFormValues';
import { yupResolver } from '@hookform/resolvers/yup';
import CommodityModelHelper from '../../../../@types/model/masterData/commodity/commodityModelHelper';
import { ICommodityConditionRelFormValues } from '../../../../@types/model/masterData/commodityConditionRel/commodityConditionRelFormValues';
import CommodityConditionRelModelHelper from '../../../../@types/model/masterData/commodityConditionRel/commodityConditionRelModelHelper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import OptionType from '../../../../@types/model/optionType';

const COMMODITY_CONDITION_EDIT = 'COMMODITY_CONDITION_EDIT';

interface ICommodityFormProps {
    isLoading : boolean;
    initialValues : ICommodityFormValues;
    isImported : boolean;
    onSubmit : (values : ICommodityFormValues) => void;
    onClose : () => void;
}

const CommodityForm = (props : ICommodityFormProps) : React.ReactElement => {
    const dispatch = useAppDispatch();

    const auth = useAppSelector(x => x.auth);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const allCommodityConditions = useAppSelector(x => x.masterData.commodityConditions);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [selectedCommodityCondition, setSelectedCommodityCondition] = useState<ICommodityCondition | null>(null);
    const [isConditionDialogOpen, setIsConditionDialogOpen] = useState<boolean>(false);
    const [isConditionLoading, setIsConditionLoading] = useState<boolean>(false);

    const schema = CommodityModelHelper.formSchema();

    const { handleSubmit,
        control,
        formState: { isValid, isDirty },
        setValue,
        watch,
        trigger
    } = useForm<ICommodityFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const currentConditionBridges = watch('commodityConditionBridges');

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onConditionSubmit = async (values : ICommodityConditionFormValues) => {
        setIsConditionLoading(true);
        try {
            const upsert = CommodityConditionModelHelper.createUpsert(values);
            const res = await CommodityConditionHttpService.addOrUpdateCommodityCondition(upsert);
            const newCommodityConditionList = ArrayHelper.upsertElement(allCommodityConditions, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodityConditions(newCommodityConditionList)); // Update global state

            generalShowSuccessSnackbar(selectedCommodityCondition ? 'Commodity Condition updated successfully.' : 'Commodity Condition added successfully.');
            closeConditionDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating Commodity Condition data.');
        } finally {
            setIsConditionLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onDragEnd = (a : DropResult) => {
        const startIndex = a.source.index;
        const newIndex = (a.destination?.index ?? 0);

        if (a.destination && (startIndex !== newIndex)) {
            const reorderedConditionBridges = lodash.orderBy([...currentConditionBridges], x => x.order, 'asc');
            const index = reorderedConditionBridges.findIndex(x => x.order === (startIndex + 1));
            if (index !== -1) {
                const conditionBridge = { ...reorderedConditionBridges[index] };
                reorderedConditionBridges.splice(index, 1);
                reorderedConditionBridges.splice(newIndex, 0, conditionBridge);
                reorderedConditionBridges.forEach((bridge, i) => {
                    const selectedCondition = allCommodityConditions.find(x => x.id === Number(bridge.condition?.value ?? 0));

                    bridge.order = i + 1;
                    bridge.condition = {
                        label: `(${i + 1}) ${selectedCondition?.condition ?? '<spacer>'}`,
                        value: bridge.condition?.value ?? 0,
                    };
                });
                setValue('commodityConditionBridges', reorderedConditionBridges, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
            }
        }
    };

    const addSpacer = () => {
        const newOrder = currentConditionBridges.length + 1;
        const commodityOption = commodities.find(x => x.id === props.initialValues.id) ?? null;
        const newSpacerOption = { value: -1 * newOrder, label: `(${newOrder}) <spacer>` };
        const spacerRel = CommodityConditionRelModelHelper.createNewFormValue(newOrder, commodityOption, newSpacerOption, true);
        const updatedConditionBridgeList = [...currentConditionBridges, spacerRel];
        setValue('commodityConditionBridges', updatedConditionBridgeList, { shouldDirty: true, shouldValidate: true });
    };

    const onSelectedConditionsChange = (selectedOptions : Array<IOptionType>) : void => {
        let updatedBridges : Array<ICommodityConditionRelFormValues> = [];

        selectedOptions
            .forEach((option, index) => {
                const existingBridge = currentConditionBridges.find(bridge => bridge.condition?.value === option.value);

                const newOrder = index + 1;
                const selectedCondition = allCommodityConditions.find(x => x.id === Number(option.value));
                const commodityOption = commodities.find(x => x.id === props.initialValues.id) ?? null;
                const newSpacerOption = {
                    label: `(${newOrder}) ${selectedCondition?.condition}`,
                    value: selectedCondition?.id ?? 0,
                };
                const updatedBridge : ICommodityConditionRelFormValues = existingBridge
                    ? {
                        ...existingBridge,
                        order: newOrder,
                        condition: {
                            label: `(${newOrder}) ${selectedCondition?.condition ?? '<spacer>'}`,
                            value: existingBridge.condition?.value ?? 0,
                        }
                    }
                    : CommodityConditionRelModelHelper.createNewFormValue(newOrder, commodityOption, newSpacerOption, false);
                updatedBridges = ArrayHelper.addElement(updatedBridges, updatedBridge, 'end');
            });
        setValue('commodityConditionBridges', updatedBridges, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    };

    const openConditionDialog = (condition : ICommodityCondition | null) => {
        setSelectedCommodityCondition(condition);
        setIsConditionDialogOpen(true);
    };

    const closeConditionDialog = () => {
        setSelectedCommodityCondition(null);
        setIsConditionDialogOpen(false);
    };

    const onConditionReset = () => {
        closeConditionDialog();
    };

    const getConditionRows = (conditionId : number | null) => {
        const value = allCommodityConditions.find(x => x.id === conditionId)?.condition ?? '';
        return value.split(/\r\n|\r|\n/).length;
    };

    const getCommodityConditionName = (conditionId : number) => {
        return allCommodityConditions.find(x => x.id === conditionId)?.condition;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const hasCommodityConditionEditingRight = useMemo<boolean>(() => {
        const rights = auth.session?.user.rights || [];
        return rights.some(x => x.code === COMMODITY_CONDITION_EDIT && x.isActive);
    }, [auth]);

    const getConditionTotalRows = useMemo<number>(() => {
        let total = 0;
        currentConditionBridges.forEach((x) => {
            return total += getConditionRows(x.condition?.value ? Number(x.condition.value) : null);
        });
        return total;
    }, [currentConditionBridges]);

    const organizationOptions = useMemo<Array<IOptionType>>(() => {
        return organizations.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [organizations]);

    const availableCommodityConditionOptions = useMemo(() => {
        return allCommodityConditions.filter(x => x.isActive)
            .filter(x => (getConditionRows(x.id) + getConditionTotalRows) <= MAX_COMMODITY_CONDITION_COLUMNS)
            .map(x => ({ label: x.condition, value: x.id }));
    }, [allCommodityConditions, getConditionTotalRows, getConditionRows]);

    const initialConditionFormValues = useMemo<ICommodityConditionFormValues | null>(() => {
        if (isConditionDialogOpen) {
            return CommodityConditionModelHelper.createFormValues(selectedCommodityCondition, commodities);
        }
        return null;
    }, [selectedCommodityCondition, isConditionDialogOpen, commodities]);

    const currentSelectedConditionOptions = useMemo<Array<IOptionType>>(() => {
        return lodash.chain(currentConditionBridges)
            .filter(x => !!x.condition)
            .orderBy(x => x.order)
            .map(x => x.condition as IOptionType)
            .value();
    }, [currentConditionBridges]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    const renderPDF = useMemo<JSX.Element>(() => {
        if (currentConditionBridges.length > 0) {
            const conditionBridges : Array<ICommodityConditionRel> = currentConditionBridges.map((bridge) => {
                return {
                    id: bridge.id,
                    guid: bridge.guid,
                    commodityId: Number(bridge.commodity?.value ?? 0),
                    conditionId: bridge.condition
                        ? Number(bridge.condition.value)
                        : null,
                    order: bridge.order,
                    isSpacer: bridge.isSpacer,
                    isActive: bridge.isActive,
                    createdById: 0,
                    createdByName: '',
                    createdOn: '',
                    updatedById: 0,
                    updatedByName: '',
                    updatedOn: '',
                };
            });
            return (
                <div className={'commodityPDFpreview-parent'}>
                    <PDFViewer className={'commodityPDFpreview'}>
                        <Document>
                            <Page style={{
                                flex: 1,
                                paddingLeft: 25,
                                paddingTop: 0.5,
                                paddingRight: 25
                            }}>
                                {renderCommodityConditionsHeader(undefined, lodash.orderBy(conditionBridges, x => x.order))}
                            </Page>
                        </Document>
                    </PDFViewer>
                </div>
            );
        } else {
            return <div/>;
        }
    }, [currentConditionBridges]);

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdr p20'}>
                    <div className={'fdc flx1 mr20 mt8'}>
                        <CustomTooltip title={props.isImported ? 'Cannot change this field if commodity is imported' : ''}>
                            <ControlledTextField
                                label={'Code'}
                                name={'code'}
                                className={'flx1 mb20'}
                                control={control}
                                disabled={props.isImported}
                                size={'small'}
                            />
                        </CustomTooltip>
                        <CustomTooltip title={props.isImported ? 'Cannot change this field if commodity is imported' : ''}>
                            <ControlledTextField
                                label={'Name'}
                                name={'name'}
                                className={'flx1 mb20'}
                                control={control}
                                disabled={props.isImported}
                                size={'small'}
                            />
                        </CustomTooltip>
                        <ControlledTextField
                            label={'Export Code'}
                            name={'exportCode'}
                            className={'mb10'}
                            control={control}
                            size={'small'}
                        />
                        <ControlledMuiAutoCompleteSelect
                            label={'Organizations'}
                            name={'organizations'}
                            control={control}
                            options={organizationOptions}
                            isMulti
                            className={'mb10'}
                            defaultValues={props.initialValues.organizations}
                        />
                        <ControlledTextField
                            label={'Temperature'}
                            name={'temperature'}
                            className={'mb20'}
                            control={control}
                            size={'small'}
                        />
                        <ControlledTextField
                            label={'Temperature Regime'}
                            name={'temperatureRegime'}
                            className={'mb20'}
                            control={control}
                            size={'small'}
                        />
                        <div className={'fdr'}>
                            <CustomTooltip title='Indicates if this commodity is a stone fruit (e.g., peaches, plums, cherries)'>
                                <div className={'mr10'}>
                                    <ControlledLabelledCheckBox
                                        label={'Is Stone Fruit'}
                                        name={'isStoneFruit'}
                                        control={control}
                                        defaultValue={props.initialValues.isStoneFruit}
                                    />
                                </div>
                            </CustomTooltip>
                            <CustomTooltip title='Indicates if this commodity meets phyto sanitary compliance requirements'>
                                <div className={'mr10'}>
                                    <ControlledLabelledCheckBox
                                        label={'Is Phyto Compliant'}
                                        name={'isPhytoCompliant'}
                                        control={control}
                                        defaultValue={props.initialValues.isPhytoCompliant}
                                    />
                                </div>
                            </CustomTooltip>
                            <CustomTooltip title={props.isImported ? 'Cannot change this field if commodity is imported' : 'Toggle whether this commodity is active in the system'}>
                                <ControlledLabelledCheckBox
                                    name={'isActive'}
                                    label={'Is Active'}
                                    control={control}
                                    defaultValue={props.initialValues.isActive}
                                    disabled={props.isImported || props.initialValues.isActive}
                                />
                            </CustomTooltip>
                        </div>
                    </div>
                    <div className={'fdc flx1 mr20'}>
                        <div className={'fdc aifs pb20'}>
                            <MuiAutocompleteSelect
                                label={'Commodity Conditions'}
                                name={'commodityConditionOptions'}
                                options={availableCommodityConditionOptions}
                                value={currentSelectedConditionOptions}
                                isMulti
                                className={'flx1 wfill'}
                                onChange={(values) => onSelectedConditionsChange(values as Array<IOptionType>)}
                            />
                            <Button
                                className={'fwb h35'}
                                startIcon={<Icon>add_circle</Icon>}
                                color={'primary'}
                                onClick={() => openConditionDialog(null)}
                                disabled={!hasCommodityConditionEditingRight}
                            >
                                Add New Condition
                            </Button>
                            <Button
                                className={'fwb h35'}
                                startIcon={<Icon>add_circle</Icon>}
                                variant={'text'}
                                color={'primary'}
                                onClick={addSpacer}
                            >
                                Add Spacer
                            </Button>
                        </div>
                        <div className={'fdr pt20 pb10 aic'}>
                            <Typography color={'primary'} variant={'h6'}>PREVIEW</Typography>
                            <div className={'flx1'}/>
                            <Typography variant={'subtitle2'}>{`ROWS: ${getConditionTotalRows} / ${MAX_COMMODITY_CONDITION_COLUMNS}`}</Typography>
                        </div>
                        <div className={'fdr flx1'}>
                            {renderPDF}
                        </div>
                        <div className={'fdr ml10 ais jcfe pt10 pb10'}>
                            <Button
                                className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                                CANCEL
                            </Button>
                            <PillButton
                                className={'pl30 pr30'}
                                text={'Save'}
                                size={'small'}
                                type={'submit'}
                                color={'secondary'}
                                disabled={!isDirty || !isValid || props.isLoading}
                            />
                        </div>
                    </div>
                    <div className={'fdc flx1'}>
                        <Typography color={'primary'} variant={'h6'}>FINDING SHEET CONDITIONS</Typography>
                        <div className={'fdc wfill'}>
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId={'complianceConditions'}>
                                    {dropProvided => <div className={'hfill'} ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                        <Card className={'fdc h600 wfill'}>
                                            {lodash.orderBy(currentConditionBridges, x => x.order).map((x, index) => // Use index for Draggable index prop
                                                <Draggable index={index} draggableId={x.order.toString()} key={`condition_drag_${x.order.toString() + x.isSpacer + x.condition?.value}`}>
                                                    {dragProvided => <div
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        {...dragProvided.dragHandleProps}>
                                                        <div className={'m5 p5 PaperBorder fdc DispatchListItem'} >
                                                            {x.isSpacer ?
                                                                `(${x.order}) <spacer>`
                                                                :
                                                                `(${x.order}) ${x.condition ? getCommodityConditionName(Number(x.condition.value)) : ''}`
                                                            }
                                                        </div>

                                                    </div>
                                                    }
                                                </Draggable>)
                                            }
                                            <div className={'dn' }>{dropProvided.placeholder}</div>
                                        </Card>
                                    </div>}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    </div>
                </div>
            </form>
            <ProjectDialog
                title={selectedCommodityCondition ? 'Edit Commodity Condition' : 'Add Commodity Condition'}
                isLoadingCircular={isConditionLoading}
                isOpen={isConditionDialogOpen}
                onClose={closeConditionDialog}
                maxWidth={'sm'}
                fullWidth
            >
                {isConditionDialogOpen && initialConditionFormValues &&
                    <CommodityConditionForm
                        initialValues={initialConditionFormValues}
                        onSubmit={onConditionSubmit}
                        onClose={onConditionReset}
                        isLoading={isConditionLoading}
                        isMasterView={false}
                    />
                }
            </ProjectDialog>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default CommodityForm;
